// ── 홍보물 주문 이력 모달 ────────────────────────────────────────────────────
import React from 'react';
import { toKoreanDateTimeString } from '../../../utils/date';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import {
  overlayStyle, modalBoxStyle, modalHeaderStyle, modalHeaderIconBox, modalHeaderTitleStyle, closeBtnStyle,
  modalBodyStyle, modalFooterStyle, cancelBtnStyle,
} from './styles';

// 이력 action 한국어 라벨
function actionLabel(action) {
  const map = {
    order_created:        '주문 등록',
    order_updated:        '주문 수정',
    order_deleted:        '주문 삭제',
    memo_updated:         '메모 수정',
    items_changed:        '아이템 변경',
    item_ordered:         '발주 처리',
    item_order_cancelled: '발주 취소',
    shipping_updated:     '송장 입력',
    item_cancelled:       '아이템 취소',
    item_restored:        '아이템 복원',
    step_changed:         '단계 변경',
    checked_step_toggled: '단계 체크',
    started:              '작업 시작',
    start_cancelled:      '시작 취소',
  };
  return map[action] || action;
}

function actionColor(action) {
  // 다크모드 대응: 반투명 배경색 사용
  if (['item_cancelled', 'order_deleted'].includes(action)) return { bg: 'rgba(239,68,68,0.12)', text: '#ef4444' };
  if (['item_restored', 'order_created'].includes(action)) return { bg: 'rgba(34,197,94,0.12)', text: '#16a34a' };
  if (['item_ordered', 'shipping_updated'].includes(action)) return { bg: 'var(--bg-subtle)', text: '#7c3aed' };
  if (['item_order_cancelled', 'start_cancelled'].includes(action)) return { bg: 'rgba(234,179,8,0.12)', text: '#d97706' };
  if (['memo_updated', 'order_updated', 'items_changed'].includes(action)) return { bg: 'rgba(14,165,233,0.12)', text: '#0284c7' };
  if (['step_changed', 'checked_step_toggled'].includes(action)) return { bg: 'rgba(34,197,94,0.1)', text: '#16a34a' };
  if (['started'].includes(action)) return { bg: 'rgba(59,130,246,0.1)', text: '#3b82f6' };
  return { bg: 'var(--bg-subtle)', text: 'var(--text-sub)' };
}

/**
 * 변경 내용 렌더링 — old_value/new_value 비교 형태 or 단순 표시
 * old + new 모두 객체이면 "필드: 이전값 → 새값" 형태로 표시
 */
function DiffDisplay({ oldValue, newValue, action }) {
  const old = oldValue && typeof oldValue === 'object' ? oldValue : null;
  const nw  = newValue && typeof newValue === 'object' ? newValue : null;

  // 삭제 이력: old만 존재 (삭제 전 데이터 표시)
  if (old && !nw) {
    return (
      <div style={{ fontSize: 11, color: '#dc2626', marginTop: 3 }}>
        {Object.entries(old).map(([k, v]) => (
          <div key={k}><span style={{ fontWeight: 600, color: 'var(--text-sub)' }}>{k}:</span> {String(v ?? '')}</div>
        ))}
      </div>
    );
  }

  // 비교 형태: old와 new 모두 객체 → "필드: 이전값 → 새값"
  if (old && nw) {
    const keys = Object.keys(nw);
    return (
      <div style={{ fontSize: 11, marginTop: 3 }}>
        {keys.map((k) => (
          <div key={k} style={{ marginBottom: 1, lineHeight: 1.5 }}>
            <span style={{ fontWeight: 600, color: 'var(--text-sub)' }}>{k}: </span>
            <span style={{ color: '#dc2626', textDecoration: 'line-through', opacity: 0.8 }}>{String(old[k] ?? '(없음)')}</span>
            <span style={{ color: 'var(--text-muted)', margin: '0 4px' }}>→</span>
            <span style={{ color: '#059669', fontWeight: 600 }}>{String(nw[k] ?? '(없음)')}</span>
          </div>
        ))}
      </div>
    );
  }

  // 등록 이력 등: new만 존재
  if (nw) {
    return (
      <div style={{ fontSize: 11, color: 'var(--text-sub)', marginTop: 3 }}>
        {Object.entries(nw)
          .filter(([, v]) => v && String(v) !== '(없음)')
          .map(([k, v]) => (
            <div key={k}><span style={{ fontWeight: 600 }}>{k}:</span> {String(v)}</div>
          ))
        }
      </div>
    );
  }

  // 문자열 등 기타 형태
  if (newValue) {
    return <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{String(newValue)}</div>;
  }

  return null;
}

export function HistoryModal({ order, data, loading, onClose }) {
  useBodyScrollLock(Boolean(order));
  const overlayCloseProps = useOverlayClickClose(onClose);
  if (!order) return null;
  return (
    <div style={overlayStyle} {...overlayCloseProps}>
      <div style={{ ...modalBoxStyle, maxWidth: 600 }} onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div style={modalHeaderStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={modalHeaderIconBox}>⏰</div>
            <span style={modalHeaderTitleStyle}>이력 — {order.company_name || '-'}</span>
          </div>
          <button style={closeBtnStyle} onClick={onClose}>✕</button>
        </div>
        {/* 바디 */}
        <div style={{ ...modalBodyStyle, gap: 6 }}>
          {loading
            ? <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>불러오는 중...</div>
            : data.length === 0
              ? <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>이력이 없습니다.</div>
              : data.map((h) => {
                  const col = actionColor(h.action);
                  // toKoreanDateTimeString: utils/date.js 사용 (new Date 직접 사용 금지)
                  const dt  = h.changed_at ? toKoreanDateTimeString(h.changed_at) : '';
                  return (
                    <div key={h.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', background: 'var(--bg-subtle)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      {/* 액션 칩 */}
                      <span style={{ fontSize: 11, fontWeight: 700, background: col.bg, color: col.text, borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {actionLabel(h.action)}
                      </span>
                      {/* 상세 */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* 아이템명 (아이템 단위 이력일 때 표시) */}
                        {h.item_name && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>{h.item_name}</div>}
                        {/* 작업자 */}
                        {h.changed_by && <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>담당: {h.changed_by}</div>}
                        {/* 변경 내용 — 비교 형태 또는 단순 표시 */}
                        <DiffDisplay oldValue={h.old_value} newValue={h.new_value} action={h.action} />
                      </div>
                      {/* 시각 */}
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>{dt}</span>
                    </div>
                  );
                })
          }
        </div>
        <div style={modalFooterStyle}>
          <button style={cancelBtnStyle} onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}
